import React, { useState } from "react";
import { FiPlus } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { Pagination } from "newui/components/Pagination";
import { Modal } from "newui/components/ui/Modal";
import type { UserListItem, UserListFilters } from "../../types/userInvite";
import { UserListTable } from "./UserListTable/UserListTable";
import { UserListFiltersComponent } from "./UserListTable/UserListFilters";
import { InviteUserDrawer } from "./InviteUserDrawer";
import { EditUserDrawer } from "./EditUserDrawer";
import { useUsersQuery } from "./hooks/useUsersQuery";
import { useDeactivateUserMutation } from "./hooks/useDeactivateUserMutation";

export const UserListPage: React.FC = () => {
  const [filters, setFilters] = useState<UserListFilters>({ page: 1 });
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UserListItem | null>(null);

  const { data, isLoading } = useUsersQuery(filters);
  const deactivate = useDeactivateUserMutation();

  const users = data?.data ?? [];

  const handleConfirmDeactivate = () => {
    if (!deactivateTarget) {
      return;
    }
    deactivate.mutate(deactivateTarget.id, {
      onSettled: () => setDeactivateTarget(null),
    });
  };

  return (
    <div className="rounded-lg bg-white shadow-sm border border-gray-200">
      <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-500">Manage your company users and their access.</p>
        </div>
        <Button
          onClick={() => setIsInviteDrawerOpen(true)}
          className="flex items-center gap-2"
          data-testid="invite-user-button"
        >
          <FiPlus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      <UserListFiltersComponent filters={filters} onChange={(f) => setFilters({ ...f, page: 1 })} />

      <UserListTable
        users={users}
        isLoading={isLoading}
        onEdit={(user) => setEditTarget(user)}
        onDeactivate={(user) => setDeactivateTarget(user)}
      />

      <Pagination
        currentPage={filters.page ?? 1}
        rowsPerPage={data?.meta?.per_page ?? 25}
        totalItems={data?.meta?.total ?? 0}
        onPageChange={(newPage) => setFilters((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={() => undefined}
        disabled={isLoading}
      />

      <InviteUserDrawer
        isOpen={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
      />

      <EditUserDrawer
        user={editTarget}
        isOpen={editTarget !== null}
        onClose={() => setEditTarget(null)}
      />

      {deactivateTarget && (
        <Modal
          isOpen
          modalType="alert"
          iconType="warning"
          title={`Deactivate ${deactivateTarget.first_name}?`}
          description="This user will no longer be able to log in. You can reactivate them later."
          primaryButton={{
            text: "Deactivate",
            isLoading: deactivate.isPending,
            disabled: deactivate.isPending,
            onClick: handleConfirmDeactivate,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setDeactivateTarget(null),
          }}
          onClose={() => setDeactivateTarget(null)}
        >
          <></>
        </Modal>
      )}
    </div>
  );
};
